import { UserId } from "@custom-types/base-types";
import { SdbMemberTypes } from "@custom-types/member-types";
import { createSelector } from "@reduxjs/toolkit";
import { MembersState } from "@src/store/members/members-slice-helper";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { membersAdapter } from "@store/members/members-slice";
import { RootState } from "@store/store-helper";
import { getPrettyName } from "@utils/user-utils";

/**
 * Returns all the stored members. It filter out the teams and only return the members
 */
export const companyMembersSelector: (state: RootState) => SdbMemberTypes[] =
  createSelector(
    (state: RootState) => state.members,
    (state) => {
      return membersAdapter
        .getSelectors()
        .selectAll(state)
        .filter((member) => member.kind === "user");
    }
  );

/**
 * Returns all the stored teams as member type. It filter out the members and only return the teams
 */
export const companyTeamsAsMemberSelector: (
  state: RootState
) => SdbMemberTypes[] = createSelector(
  (state: RootState) => state.members,
  (members) => {
    return membersAdapter
      .getSelectors()
      .selectAll(members)
      .filter((member) => member.kind === "user-group");
  }
);

/**
 * Returns all the stored members filtered by search text.
 */
export const filteredCompanyMembersSelector: (
  state: RootState
) => SdbMemberTypes[] = createSelector(
  (state: RootState) => state.ui.search.debouncedSearchText,
  companyMembersSelector,
  (searchText, members) => {
    const searchTextLower = searchText.toLowerCase();
    return members.filter((member) => {
      return (
        member.email.toLowerCase().includes(searchTextLower) ||
        // Use pretty name, in case the user types the full name in the search bar.
        getPrettyName(member).toLowerCase().includes(searchTextLower)
      );
    });
  }
);

/**
 * Returns the fetching properties of the members slice.
 */
export const fetchingMembersFlagsSelector: (
  state: RootState
) => MembersState["fetching"] = createSelector(
  (state: RootState) => state.members,
  (members) => {
    return members.fetching;
  }
);

/**
 * Returns the fetched properties of the members slice.
 */
export const fetchedMembersFlagsSelector: (
  state: RootState
) => MembersState["fetched"] = createSelector(
  (state: RootState) => state.members,
  (members) => {
    return members.fetched;
  }
);

/**
 * Get company member with the provided role
 */
export function companyMembersByRoleSelector(
  userCompanyRole: FaroCoreApiTypes.CompanyRole
): (state: RootState) => SdbMemberTypes[] {
  return createSelector(companyMembersSelector, (members) => {
    return members.filter(({ role }) => role === userCompanyRole);
  });
}

/**
 * Get single member with the provided member id.
 * The id is not the actual member id, but the id used to store the member in the store,
 * which is either identity or email.
 *
 * @see getMemberId
 */
export function companyMemberSelector(
  memberId: UserId | undefined
): (state: RootState) => SdbMemberTypes | null {
  return createSelector(
    (state: RootState) => state.members,
    (members) => {
      if (!memberId) {
        return null;
      }
      return (
        membersAdapter.getSelectors().selectById(members, memberId) ?? null
      );
    }
  );
}
